/**
 * AdminSettingsPage
 * Allows admins to configure Copilot analysis settings stored in WVHealth_CoreSettings SP list.
 * Full-page layout with branded header/footer, collapsible settings panels.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Field,
    Input,
    Dropdown,
    Option,
    Spinner,
    Switch,
    MessageBar,
    MessageBarBody,
    makeStyles,
    tokens
} from '@fluentui/react-components';
import {
    Save24Regular,
    ArrowReset24Regular,
    ChevronDown20Regular,
    ChevronUp20Regular,
    Settings24Filled,
    Brain24Regular,
    TextGrammarArrowLeft24Regular,
    Trophy24Regular,
    Table24Regular,
    SearchInfo24Regular,
    Warning24Regular,
    CheckmarkCircle24Regular,
    Info16Regular,
    FolderProhibited24Regular,
    DocumentCheckmark24Regular,
    Add24Regular,
    Dismiss16Regular,
    Search24Regular,
} from '@fluentui/react-icons';
import { coreSettingsService, CopilotAnalysisConfig, DEFAULT_COPILOT_CONFIG } from '../services/CoreSettingsService';
import { useLicense } from '../hooks/useLicense';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import PageHero from '../components/layout/PageHero';
import { useMsal } from '@azure/msal-react';
import { LocalIndexService } from '../services/LocalIndexService';
import { useMemo } from 'react';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    page: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F3F4F6',
    },
    main: {
        flex: '1',
        maxWidth: '860px',
        margin: '0 auto',
        width: '100%',
        padding: '32px 16px 48px',
    },
    // Breadcrumb row
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        marginBottom: '20px',
    },
    breadcrumbText: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    // Section card container
    sectionCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: '12px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        transition: 'box-shadow 0.2s',
    },
    // Collapsible header row
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        cursor: 'pointer',
        userSelect: 'none',
        borderBottom: '1px solid transparent',
        transition: 'background-color 0.15s',
        ':hover': {
            backgroundColor: '#f9fafb',
        },
    },
    sectionHeaderOpen: {
        borderBottomColor: '#e5e7eb',
    },
    sectionHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    sectionIconWrap: {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: '0',
    },
    sectionTitleGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: '#111827',
        margin: '0',
    },
    sectionSubtitle: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        margin: '0',
    },
    sectionBadge: {
        fontSize: '11px',
        fontWeight: '600',
        padding: '2px 8px',
        borderRadius: '999px',
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
    },
    // Section body
    sectionBody: {
        padding: '20px 24px 24px',
    },
    formulaNote: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        marginBottom: '16px',
        marginTop: '0',
        padding: '8px 12px',
        backgroundColor: '#f0fdf4',
        borderLeft: '3px solid #22c55e',
        borderRadius: '0 6px 6px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        '@media (min-width: 600px)': {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    grid3: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        '@media (min-width: 600px)': {
            gridTemplateColumns: '1fr 1fr',
        },
        '@media (min-width: 860px)': {
            gridTemplateColumns: '1fr 1fr 1fr',
        },
    },
    // Impact tag
    impactTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        fontWeight: '600',
        padding: '2px 8px',
        borderRadius: '999px',
        marginTop: '8px',
    },
    // Actions bar
    actionsBar: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        border: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalM,
        marginTop: '20px',
    },
    actionsLeft: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
    },
    actionsHint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    folderInputRow: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
        alignItems: 'flex-end',
        marginBottom: '12px',
    },
    tagList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        minHeight: '32px',
    },
    tagChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px 4px 12px',
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '999px',
        fontSize: tokens.fontSizeBase200,
        color: '#0369a1',
        fontWeight: tokens.fontWeightSemibold,
    },
});

// ─── CollapsibleSection ───────────────────────────────────────────────────────

interface CollapsibleSectionProps {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    title: string;
    subtitle: string;
    badge?: string;
    impact?: string;
    impactColor?: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}

function CollapsibleSection({
    icon, iconBg, iconColor, title, subtitle, badge, impact, impactColor = '#6b7280', defaultOpen = false, children
}: CollapsibleSectionProps) {
    const styles = useStyles();
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className={styles.sectionCard}>
            <div
                className={`${styles.sectionHeader} ${open ? styles.sectionHeaderOpen : ''}`}
                onClick={() => setOpen(o => !o)}
                role="button"
                aria-expanded={open}
            >
                <div className={styles.sectionHeaderLeft}>
                    <div className={styles.sectionIconWrap} style={{ backgroundColor: iconBg, color: iconColor }}>
                        {icon}
                    </div>
                    <div className={styles.sectionTitleGroup}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h2 className={styles.sectionTitle}>{title}</h2>
                            {badge && (
                                <span className={styles.sectionBadge}>{badge}</span>
                            )}
                        </div>
                        <p className={styles.sectionSubtitle}>{subtitle}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {impact && (
                        <span
                            className={styles.impactTag}
                            style={{ backgroundColor: impactColor + '18', color: impactColor }}
                        >
                            <Warning24Regular style={{ width: 12, height: 12 }} />
                            {impact}
                        </span>
                    )}
                    {open
                        ? <ChevronUp20Regular style={{ color: '#6b7280' }} />
                        : <ChevronDown20Regular style={{ color: '#6b7280' }} />
                    }
                </div>
            </div>
            {open && (
                <div className={styles.sectionBody}>
                    {children}
                </div>
            )}
        </div>
    );
}

// ─── AdminSettingsPage ────────────────────────────────────────────────────────

export default function AdminSettingsPage() {
    const styles = useStyles();
    const navigate = useNavigate();
    const { accounts, instance } = useMsal();
    const { license, isLoading: siteLoading } = useLicense();
    const primarySiteUrl = license?.allowedSiteUrls?.[0] || '';

    const userName = accounts[0]?.name || accounts[0]?.username?.split('@')[0] || 'User';
    const indexService = useMemo(() => new LocalIndexService(), []);

    const [config, setConfig] = useState<CopilotAnalysisConfig>(DEFAULT_COPILOT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [newFolder, setNewFolder] = useState('');

    // Load settings on mount
    useEffect(() => {
        if (!primarySiteUrl || siteLoading) return;
        (async () => {
            try {
                const loaded = await coreSettingsService.getSettings(primarySiteUrl);
                setConfig(loaded);
            } catch {
                setMessage({ type: 'error', text: 'Failed to load settings. Showing defaults.' });
            } finally {
                setLoading(false);
            }
        })();
    }, [primarySiteUrl, siteLoading]);

    const handleSave = async () => {
        if (!primarySiteUrl) return;
        setSaving(true);
        setMessage(null);
        try {
            await coreSettingsService.saveSettings(primarySiteUrl, config);
            setMessage({ type: 'success', text: 'Settings saved successfully. Changes will apply on the next scan.' });
        } catch {
            setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setConfig({ ...DEFAULT_COPILOT_CONFIG });
        setMessage({ type: 'success', text: 'Reset to defaults. Click Save Settings to apply.' });
    };

    const set = <K extends keyof CopilotAnalysisConfig>(key: K, val: CopilotAnalysisConfig[K]) =>
        setConfig(prev => ({ ...prev, [key]: val }));

    const setNested = <P extends keyof CopilotAnalysisConfig>(
        parent: P,
        key: string,
        val: number
    ) => setConfig(prev => ({
        ...prev,
        [parent]: { ...(prev[parent] as object), [key]: val }
    }));

    const addFolder = () => {
        const trimmed = newFolder.trim();
        if (!trimmed || config.excludedFolderPaths.includes(trimmed)) return;
        set('excludedFolderPaths', [...config.excludedFolderPaths, trimmed]);
        setNewFolder('');
    };

    const removeFolder = (folder: string) =>
        set('excludedFolderPaths', config.excludedFolderPaths.filter(f => f !== folder));

    // ── Loading state
    if (loading || siteLoading) {
        return (
            <div className={styles.page}>
                <Header
                    isAuthenticated={true}
                    userName={userName}
                    onSignOut={() => instance.logoutPopup()}
                    localIndexService={indexService}
                />
                <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spinner label="Loading settings from SharePoint..." />
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* ── Branded Header ── */}
            <Header
                isAuthenticated={true}
                userName={userName}
                onSignOut={() => instance.logoutPopup()}
                siteUrl={primarySiteUrl}
                localIndexService={indexService}
            />

            <PageHero
                icon={<Settings24Filled style={{ width: 22, height: 22 }} />}
                eyebrow={{ label: 'Admin Configuration' }}
                title='Copilot Analysis Settings'
                subtitle={
                    <>
                        Stored in <span style={{ color: '#2ba6bb', fontWeight: 600 }}>WVHealth_CoreSettings</span> SharePoint list · Applied on next scan
                    </>
                }
                breadcrumbs={[{ label: 'Back', onClick: () => navigate(-1) }]}
                rightSlot={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'rgba(43,166,187,0.12)', borderRadius: '10px', border: '1px solid rgba(43,166,187,0.25)', fontSize: '13px', color: '#67e8f9', whiteSpace: 'nowrap' }}>
                        <CheckmarkCircle24Regular style={{ width: 18, height: 18 }} />
                        <span>Changes apply on next scan only</span>
                    </div>
                }
            />

            {/* ── Main Content ── */}
            <main className={styles.main}>

                {/* Status message */}
                {message && (
                    <MessageBar
                        intent={message.type === 'success' ? 'success' : 'error'}
                        style={{ marginBottom: 16, borderRadius: 8 }}
                    >
                        <MessageBarBody>{message.text}</MessageBarBody>
                    </MessageBar>
                )}

                {/* ── Section: Analysis Pipeline ── */}
                <CollapsibleSection
                    icon={<Brain24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#e0f4f7"
                    iconColor="#2ba6bb"
                    title="Analysis Pipeline"
                    subtitle="ML model, page scan limits and caching behaviour"
                    badge="Core"
                    impact="High impact"
                    impactColor="#2ba6bb"
                    defaultOpen={true}
                >
                    <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '0 6px 6px 0', fontSize: '12px', color: '#1e40af', display: 'flex', gap: 6 }}>
                        <Info16Regular style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>The <strong>Model ID</strong> controls which Hugging Face embedding model is used. Changing this will affect relevance scoring quality and may increase processing time on large sites.</span>
                    </div>
                    <div className={styles.grid}>

                        <Field label="Model ID" hint="Hugging Face model used to generate embeddings. Use the 'Xenova/' prefix for browser-compatible models.">
                            <Input
                                value={config.modelId}
                                onChange={(_, d) => set('modelId', d.value)}
                                placeholder="Snowflake/snowflake-arctic-embed-xs"
                            />
                        </Field>
                        <Field label="Max Pages to Scan" hint="Limits the number of pages retrieved from SharePoint per scan">
                            <Input
                                type="number"
                                value={String(config.maxTotalIndexItems)}
                                onChange={(_, d) => set('maxTotalIndexItems', Number(d.value) || 1000)}
                            />
                        </Field>
                        <Field label="Caching Strategy" hint="How to cache raw site pages locally for speed">
                            <Dropdown
                                value={config.cachingStrategy}
                                selectedOptions={[config.cachingStrategy]}
                                onOptionSelect={(_, d) => set('cachingStrategy', d.optionValue as any)}
                            >
                                <Option value="Local">Local (IndexedDB)</Option>
                                <Option value="Session">Session (Browser Memory)</Option>
                                <Option value="Off">Off (Always fetch SP)</Option>
                            </Dropdown>
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Readability ── */}
                <CollapsibleSection
                    icon={<TextGrammarArrowLeft24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#fdf4ff"
                    iconColor="#7c3aed"
                    title="Readability: Flesch Reading Ease"
                    subtitle="Clamp the range of valid Flesch scores to avoid over-penalising technical content"
                    impact="Medium impact"
                    impactColor="#7c3aed"
                >
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#fdf4ff', borderLeft: '3px solid #7c3aed', borderRadius: '0 6px 6px 0' }}>
                        Scores outside the [{config.readabilityClampMin} – {config.readabilityClampMax}] window are clamped before being applied to the readiness formula, preventing extreme outliers from skewing results.
                    </p>
                    <div className={styles.grid}>
                        <Field label="Clamp minimum" hint="Scores below this value are raised up (prevents over-penalising technical content)">
                            <Input
                                type="number"
                                value={String(config.readabilityClampMin)}
                                onChange={(_, d) => set('readabilityClampMin', Number(d.value))}
                            />
                        </Field>
                        <Field label="Clamp maximum" hint="Scores above this value are reduced down">
                            <Input
                                type="number"
                                value={String(config.readabilityClampMax)}
                                onChange={(_, d) => set('readabilityClampMax', Number(d.value))}
                            />
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Readiness Score Weights ── */}
                <CollapsibleSection
                    icon={<Trophy24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#fffbeb"
                    iconColor="#f59e0b"
                    title="Readiness Score Weights"
                    subtitle="Control how structure, readability and a base offset combine into the final readiness score"
                    impact="High impact"
                    impactColor="#f59e0b"
                >
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '0 6px 6px 0', fontFamily: 'monospace' }}>
                        Readiness = Structure × <em>w₁</em> ({config.readinessWeights.structure}) + Readability × <em>w₂</em> ({config.readinessWeights.readability}) + base ({config.readinessWeights.base}).
                        &nbsp; <strong>Weights should sum to less than 1.</strong>
                    </p>
                    <div className={styles.grid3}>
                        <Field label="Structure weight (w₁)">
                            <Input type="number" inputMode="decimal"
                                value={String(config.readinessWeights.structure)}
                                onChange={(_, d) => setNested('readinessWeights', 'structure', Number(d.value))} />
                        </Field>
                        <Field label="Readability weight (w₂)">
                            <Input type="number" inputMode="decimal"
                                value={String(config.readinessWeights.readability)}
                                onChange={(_, d) => setNested('readinessWeights', 'readability', Number(d.value))} />
                        </Field>
                        <Field label="Base score (added to all pages)">
                            <Input type="number"
                                value={String(config.readinessWeights.base)}
                                onChange={(_, d) => setNested('readinessWeights', 'base', Number(d.value))} />
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Layout Complexity Thresholds ── */}
                <CollapsibleSection
                    icon={<Table24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#fff7ed"
                    iconColor="#ea580c"
                    title="Layout Complexity Thresholds"
                    subtitle="Tag density = HTML tags ÷ total words. Determines Warning or Fail flags on the dashboard"
                    impact="Medium impact"
                    impactColor="#ea580c"
                >
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '0 6px 6px 0' }}>
                        Pages with tag density &lt; <strong>{config.layoutThresholds.warning}</strong> are rated <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ Excellent</span>, between {config.layoutThresholds.warning}–{config.layoutThresholds.fail} show a <span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠ Warning</span>, and above {config.layoutThresholds.fail} show a <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ Fail</span>.
                    </p>
                    <div className={styles.grid}>
                        <Field label="Warning threshold" hint={`Tag density above this → ⚠️ Warning (current: ${config.layoutThresholds.warning})`}>
                            <Input type="number" inputMode="decimal"
                                value={String(config.layoutThresholds.warning)}
                                onChange={(_, d) => setNested('layoutThresholds', 'warning', Number(d.value))} />
                        </Field>
                        <Field label="Fail threshold" hint={`Tag density above this → ❌ Fail (current: ${config.layoutThresholds.fail})`}>
                            <Input type="number" inputMode="decimal"
                                value={String(config.layoutThresholds.fail)}
                                onChange={(_, d) => setNested('layoutThresholds', 'fail', Number(d.value))} />
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Keyword Stuffing ── */}
                <CollapsibleSection
                    icon={<SearchInfo24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#fef2f2"
                    iconColor="#dc2626"
                    title="Keyword Stuffing Detection"
                    subtitle="Type-Token Ratio and n-gram frequency signals used to detect repetitive or spammy content"
                    impact="Low impact"
                    impactColor="#dc2626"
                >
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#fef2f2', borderLeft: '3px solid #dc2626', borderRadius: '0 6px 6px 0' }}>
                        A low <strong>TTR</strong> (unique words ÷ total words) signals repetitive content. A high top bi-gram frequency indicates keyword stuffing. Pages triggering both signals receive a stuffing flag in the quality report.
                    </p>
                    <div className={styles.grid}>
                        <Field label="TTR threshold" hint="Type-Token Ratio below this triggers a stuffing signal (lower = more repetition allowed)">
                            <Input type="number" inputMode="decimal"
                                value={String(config.keywordStuffing.ttrThreshold)}
                                onChange={(_, d) => setNested('keywordStuffing', 'ttrThreshold', Number(d.value))} />
                        </Field>
                        <Field label="Top N-gram frequency" hint="If the most common 2-gram appears more than this many times → stuffing signal">
                            <Input type="number"
                                value={String(config.keywordStuffing.ngramTopFrequency)}
                                onChange={(_, d) => setNested('keywordStuffing', 'ngramTopFrequency', Number(d.value))} />
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Excluded Folders ── */}
                <CollapsibleSection
                    icon={<FolderProhibited24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#f0fdf4"
                    iconColor="#16a34a"
                    title="Excluded Folders"
                    subtitle="Folders inside the Site Pages library whose pages are skipped during assessment (e.g. Templates)"
                    badge="Scope"
                >
                    <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#f0fdf4', borderLeft: '3px solid #16a34a', borderRadius: '0 6px 6px 0', display: 'flex', gap: 6 }}>
                        <Info16Regular style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>Enter the exact name of a folder inside the <strong>Site Pages</strong> library as it appears in SharePoint. Pages inside these folders will be skipped during assessment.</span>
                    </p>

                    <Field label="Add folder to exclude" hint="Press Enter or click Add. Folder must exist inside the Site Pages library">
                        <div className={styles.folderInputRow}>
                            <Input
                                value={newFolder}
                                onChange={(_, d) => setNewFolder(d.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFolder(); } }}
                                placeholder="e.g. Templates"
                                style={{ flex: 1 }}
                            />
                            <Button
                                appearance="secondary"
                                icon={<Add24Regular />}
                                onClick={addFolder}
                                disabled={!newFolder.trim() || config.excludedFolderPaths.includes(newFolder.trim())}
                            >
                                Add
                            </Button>
                        </div>
                    </Field>

                    {config.excludedFolderPaths.length > 0 ? (
                        <div className={styles.tagList}>
                            {config.excludedFolderPaths.map(folder => (
                                <span key={folder} className={styles.tagChip}>
                                    {folder}
                                    <button
                                        onClick={() => removeFolder(folder)}
                                        aria-label={`Remove ${folder}`}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px', display: 'flex', alignItems: 'center', color: '#64748b', borderRadius: '50%' }}
                                    >
                                        <Dismiss16Regular style={{ width: 14, height: 14 }} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, margin: 0 }}>
                            No folders excluded. All site content will be assessed.
                        </p>
                    )}
                </CollapsibleSection>

                {/* ── Section: Search Indexer ── */}
                <CollapsibleSection
                    icon={<Search24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#e0f4f7"
                    iconColor="#2ba6bb"
                    title="Search Indexer"
                    subtitle="Control when the header notification bell flags the search index as out of date"
                    badge="Notifications"
                >
                    <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#e0f4f7', borderLeft: '3px solid #2ba6bb', borderRadius: '0 6px 6px 0', display: 'flex', gap: 6 }}>
                        <Info16Regular style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>If the most recently synced search source is older than this many days, the notification bell shows a "Search index is out of date" alert.</span>
                    </p>
                    <div className={styles.grid}>
                        <Field label="Stale after (days)" hint="Number of days since the last full sync before the index is considered stale">
                            <Input
                                type="number"
                                value={String(config.searchIndexStaleDays)}
                                onChange={(_, d) => set('searchIndexStaleDays', Number(d.value) || 1)}
                            />
                        </Field>
                    </div>
                </CollapsibleSection>

                {/* ── Section: Content Versioning ── */}
                <CollapsibleSection
                    icon={<DocumentCheckmark24Regular style={{ width: 20, height: 20 }} />}
                    iconBg="#eff6ff"
                    iconColor="#2563eb"
                    title="Content Versioning"
                    subtitle="Control which page version is assessed when versioning is enabled on a library"
                    badge="Filtering"
                >
                    <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 16px', padding: '8px 12px', backgroundColor: '#eff6ff', borderLeft: '3px solid #2563eb', borderRadius: '0 6px 6px 0', display: 'flex', gap: 6 }}>
                        <Info16Regular style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>SharePoint distinguishes between pages that have <strong>never been published</strong> (draft-only) and pages that were published but have a newer <strong>unpublished draft</strong>. This setting controls which category is assessed.</span>
                    </p>

                    <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                        <Switch
                            label="Assess published versions only"
                            checked={config.assessPublishedOnly}
                            onChange={(_, d) => set('assessPublishedOnly', d.checked)}
                        />
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '10px 0 0 0', lineHeight: 1.6 }}>
                            {config.assessPublishedOnly ? (
                                <>
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>Enabled:</span> Only the latest <strong>published</strong> version is assessed.
                                    Pages that have <em>never been published</em> are excluded entirely.
                                    Pages with a newer unpublished draft are still assessed using their last published version.
                                </>
                            ) : (
                                <>
                                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>Disabled:</span> The latest version of every page is assessed regardless of publish state,
                                    including unpublished drafts and pages that have never been published.
                                </>
                            )}
                        </p>
                    </div>
                </CollapsibleSection>

                {/* ── Sticky Action Bar ── */}
                <div className={styles.actionsBar}>
                    <div className={styles.actionsHint}>
                        <Info16Regular />
                        <span>Changes are only applied when you click <strong>Save Settings</strong> and a new scan runs.</span>
                    </div>
                    <div className={styles.actionsLeft}>
                        <Button
                            appearance="secondary"
                            icon={<ArrowReset24Regular />}
                            onClick={handleReset}
                            disabled={saving}
                        >
                            Reset to Defaults
                        </Button>
                        <Button
                            appearance="primary"
                            icon={saving ? <Spinner size="tiny" /> : <Save24Regular />}
                            onClick={handleSave}
                            disabled={saving}
                            style={{ backgroundColor: '#2ba6bb', borderColor: '#2ba6bb' }}
                        >
                            {saving ? 'Saving…' : 'Save Settings'}
                        </Button>
                    </div>
                </div>

            </main>

            {/* ── Branded Footer ── */}
            <Footer />
        </div>
    );
}
